<template>
    <div
        v-if="timeLeft > 0"
        class="delivery-timeframe"
    >
        <LazyLoad when-idle>
            <I18n
                keypath="deliveryDays.timeframe.title"
                tag="p"
                class="standard-delivery"
            >
                <template #date>
                    {{ formatDate(date) }}
                </template>

                <template #validUntil>
                    <template v-if="timeLeftFormatted.days">
                        {{
                            t(
                                'deliveryDays.timeframe.days',
                                timeLeftFormatted.days
                            )
                        }}
                    </template>

                    <template v-if="timeLeftFormatted.hours">
                        {{
                            ' ' +
                            t(
                                'deliveryDays.timeframe.hours',
                                timeLeftFormatted.hours
                            )
                        }}
                    </template>

                    <template v-if="timeLeftFormatted.minutes">
                        {{
                            ' ' +
                            t(
                                'deliveryDays.timeframe.minutes',
                                timeLeftFormatted.minutes
                            )
                        }}
                    </template>

                    <template v-if="timeLeftFormatted.seconds">
                        {{
                            ' ' +
                            t(
                                'deliveryDays.timeframe.seconds',
                                timeLeftFormatted.seconds
                            )
                        }}
                    </template>
                </template>
            </I18n>
        </LazyLoad>

        <div
            v-if="supportsExpress"
            class="express-row"
        >
            <span class="express-pill">
                {{ t('catalogue.express_available') }}
            </span>

            <div class="express-footer">
                <span class="express-detail">
                    {{ t('catalogue.express_shipping_earlier') }}
                </span>

                <button
                    type="button"
                    class="express-chat"
                    @click="openSpeakToExpertModal"
                >
                    {{ t('catalogue.express_chat_button') }}
                </button>
            </div>
        </div>

        <slot name="default" />
    </div>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { DeliveryTimeframeFragment } from '@/graphql/api'
import { parseDateTime } from '@/utils/date'
import { useDateFormat } from '@/composables/useDateFormat'
import { getInterval } from '@/helpers/globals'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'

import LazyLoad from '@/components/LazyLoad.vue'

const formatDate = useDateFormat({
    month: 'long',
    day: 'numeric',
    year: 'numeric',
})

const { t } = useI18n()
const { open: openSpeakToExpertModal } = useSpeakToExpertModal()

const props = withDefaults(
    defineProps<{
        data: DeliveryTimeframeFragment
        supportsExpress?: boolean
    }>(),
    {
        supportsExpress: false,
    }
)

const date = computed(() => parseDateTime(props.data.date))
const validUntil = computed(() => parseDateTime(props.data.validUntil))

const calculate = (): number =>
    validUntil.value.getTime() - new Date().getTime()

const timeLeft: Ref<number> = ref(calculate())

const interval = getInterval()

if (interval) {
    const { setInterval, clearInterval } = interval

    const intervalId = setInterval(() => {
        timeLeft.value = calculate()
    }, 1000)

    onBeforeUnmount(() => clearInterval(intervalId))
}

const MILLISECONDS_SECOND = 1000
const MILLISECONDS_MINUTE = 60 * MILLISECONDS_SECOND
const MILLISECONDS_HOUR = 60 * MILLISECONDS_MINUTE
const MILLISECONDS_DAY = 24 * MILLISECONDS_HOUR

const timeLeftFormatted = computed(() => ({
    days: Math.floor(timeLeft.value / MILLISECONDS_DAY),
    hours: Math.floor((timeLeft.value % MILLISECONDS_DAY) / MILLISECONDS_HOUR),
    minutes: Math.floor(
        (timeLeft.value % MILLISECONDS_HOUR) / MILLISECONDS_MINUTE
    ),
    seconds: Math.floor(
        (timeLeft.value % MILLISECONDS_MINUTE) / MILLISECONDS_SECOND
    ),
}))
</script>

<style lang="scss" scoped src="./delivery-timeframe.scss"></style>
